import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createTextVNode as _createTextVNode, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "no-options" };
const _hoisted_2 = { class: "pagination" };
const _hoisted_3 = ["disabled"];
const _hoisted_4 = ["disabled"];
const _hoisted_5 = { class: "pagination__total" };
import { computed, ref, watch } from "vue";
import { createPopper } from "@popperjs/core";
import VSelect from "vue-select";
import paloma from "../../services/PalomaBackend";
import env from "../../services/AppEnv";
import { numberFilter } from "../../util/CommonUiFilters";
import { useFieldControl } from "./useFieldControl";
export default /*@__PURE__*/ _defineComponent({
    __name: 'FieldControlEntity',
    props: {
        form: {},
        validation: {},
        field: {},
        fieldId: {},
        data: {}
    },
    emits: [
        'change',
    ],
    setup(__props, { emit: __emit }) {
        const http = paloma.http;
        const emit = __emit;
        // `data` should be "FormFieldDataItem | FormFieldDataItem[]" (or an identifier), but the caller can't guarantee it
        const props = __props;
        const { fieldOptions, emitChange } = useFieldControl(props, emit, 'FieldControlEntity');
        const model = ref(undefined);
        const selectOptions = ref(Array.isArray(fieldOptions.value.data) ? fieldOptions.value.data : []);
        const optionsPage = ref(null);
        const searchQuery = ref('');
        const dataUrl = computed(() => {
            const data = fieldOptions.value.data;
            return (data && !Array.isArray(data)) ? data.url : null;
        });
        const identifierReference = computed(() => fieldOptions.value.entityReference === 'identifier');
        initModel();
        if (dataUrl.value) {
            loadOptions();
        }
        watch(() => props.data, initModel);
        function initModel() {
            model.value = identifierReference.value
                ? { id: props.data, name: props.data }
                : props.data;
            if (!model.value && fieldOptions.value.multiple) {
                model.value = [];
            }
            if (dataUrl.value) {
                loadModelOptions();
            }
            else if (Array.isArray(fieldOptions.value.data)) {
                refreshModel(fieldOptions.value.data);
            }
        }
        // TODO debounce?
        function fetchOptions(search, loading) {
            if (!dataUrl.value) {
                return;
            }
            searchQuery.value = search || '';
            if (optionsPage.value) {
                optionsPage.value.number = 0;
            }
            loading(true);
            loadOptions()
                .finally(() => loading(false));
        }
        function changePage(delta) {
            optionsPage.value.number += delta;
            loadOptions();
        }
        async function loadOptions() {
            try {
                const content = await query(searchQuery.value);
                selectOptions.value = content.items;
                if (content.page) {
                    optionsPage.value = content.page;
                }
            }
            catch (e) {
                console.error('Failed to fetch options from ' + dataUrl.value, e);
            }
        }
        /**
         * For a data source, all selected values are fetched from the server each time
         * the data changes, because the options might be more current.
         */
        function loadModelOptions() {
            const value = model.value;
            if (Array.isArray(value)) {
                return Promise.all(value.map(v => loadModelOption(v.id)));
            }
            if (value && typeof value === 'object') {
                return loadModelOption(value.id);
            }
            return Promise.resolve();
        }
        async function loadModelOption(id) {
            if (id == null) {
                return;
            }
            try {
                await query('id:' + escapeQueryArg(id));
            }
            catch (e) {
                console.error('Failed to fetch selected value with id = ' + id + ' from ' + dataUrl.value, e);
            }
        }
        /**
         * Queries the data source and refreshes the selected values with the received
         * options. The response is either a list or a `Page`.
         */
        async function query(queryString) {
            const response = await http.get(dataUrl.value, {
                params: {
                    query: queryString,
                    page: optionsPage.value?.number || 0,
                    size: optionsPage.value?.size || 10,
                    locale: env.locale,
                },
            });
            const content = Array.isArray(response.data)
                ? { items: response.data }
                : { items: response.data.content, page: response.data };
            refreshModel(content.items);
            return content;
        }
        /**
         * If received options contain selected values, the options are used instead,
         * because they might be more current.
         */
        function refreshModel(options) {
            const value = model.value;
            if (Array.isArray(value)) {
                value.forEach((item, i) => {
                    const option = item && options.find(o => o.id === item.id);
                    if (option) {
                        value[i] = option;
                    }
                });
            }
            else if (value) {
                const option = options.find(o => o.id === value.id);
                if (option) {
                    model.value = option;
                }
            }
        }
        function escapeQueryArg(id) {
            return `"${('' + id).replace(/"/g, '\\"')}"`;
        }
        function onChange(newValue) {
            const value = (identifierReference.value && newValue != null)
                ? newValue.id
                : newValue;
            emitChange(value);
        }
        function withPopper(dropdownList, component) {
            /**
             * Here we position the dropdownList relative to the $refs.toggle Element.
             *
             * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
             * the dropdownList overlap by 1 pixel.
             *
             * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
             * wrapper so that we can set some styles for when the dropdown is placed
             * above.
             *
             * The 'sameWidth' modifier ensures that the drop-down's width remains
             * equal to the width of the $refs.toggle Element (e.g. when the user
             * resizes the browser).
             */
            const popper = createPopper(component.$refs.toggle, dropdownList, {
                placement: 'bottom',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -1],
                        },
                    },
                    {
                        name: 'toggleClass',
                        enabled: true,
                        phase: 'write',
                        fn({ state }) {
                            component.$el.classList.toggle('drop-up', state.placement === 'top');
                        },
                    },
                    {
                        name: 'sameWidth',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn: ({ state }) => {
                            state.styles.popper.width = `${component.$refs.toggle.clientWidth}px`;
                        },
                        effect: ({ state }) => {
                            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                        },
                    },
                ],
            });
            /**
             * To prevent memory leaks Popper needs to be destroyed.
             * If you return function, it will be called just before dropdown is removed from DOM.
             */
            return () => popper.destroy();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(VSelect), {
                modelValue: model.value,
                "onUpdate:modelValue": onChange,
                onSearch: fetchOptions,
                inputId: __props.fieldId,
                filterable: !dataUrl.value,
                disabled: _unref(fieldOptions).disabled,
                clearable: !_unref(fieldOptions).required,
                multiple: _unref(fieldOptions).multiple,
                options: selectOptions.value,
                appendToBody: true,
                "calculate-position": withPopper,
                label: "name"
            }, _createSlots({
                "no-options": _withCtx(() => [
                    _createElementVNode("span", _hoisted_1, _toDisplayString(_ctx.$t('grid.no_search_results')), 1)
                ]),
                _: 2
            }, [
                (optionsPage.value && optionsPage.value.totalPages > 1)
                    ? {
                        name: "list-footer",
                        fn: _withCtx(() => [
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("button", {
                                    disabled: optionsPage.value.first,
                                    onClick: _cache[0] || (_cache[0] = _withModifiers(($event) => (changePage(-1)), ["prevent"])),
                                    href: "",
                                    class: "pagination__btn"
                                }, _toDisplayString(_ctx.$t('prev')), 9, _hoisted_3),
                                _cache[2] || (_cache[2] = _createTextVNode()),
                                _createElementVNode("button", {
                                    disabled: optionsPage.value.last,
                                    onClick: _cache[1] || (_cache[1] = _withModifiers(($event) => (changePage(1)), ["prevent"])),
                                    href: "",
                                    class: "pagination__btn"
                                }, _toDisplayString(_ctx.$t('next')), 9, _hoisted_4),
                                _cache[3] || (_cache[3] = _createTextVNode()),
                                _createElementVNode("span", _hoisted_5, "\n                    (" + _toDisplayString(_unref(numberFilter)(optionsPage.value.totalElements)) + " " + _toDisplayString(_ctx.$t('total')) + ")\n                ", 1)
                            ])
                        ]),
                        key: "0"
                    }
                    : undefined
            ]), 1032, ["modelValue", "inputId", "filterable", "disabled", "clearable", "multiple", "options"]));
        };
    }
});
